import React, { useMemo, useState } from 'react';
import { SendIcon, MailIcon, CheckIcon, CopyIcon, UserSearchIcon, AlertCircleIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useRegistry, type OutreachPurpose } from '../../contexts/RegistryContext';
import { formatWhen } from '../../utils/adminFormat';
import { SendDialog } from './SendDialog';
import { openCompose, getSenderEmail, setSenderEmail, getMailClient, setMailClient, mailClientLabels, type MailClient } from '../../utils/mailer';
interface Note {
  tone: 'positive' | 'neutral';
  message: string;
}
const SITE = 'https://velnorisexecutivenetwork.com';
const FIRM_EMAIL = 'hello@velnorisexecutivenetwork.com';

/** Signed off from the firm's mailbox on every outreach message. */
const SIGNATURE = `Velnoris Executive Network
velnorisexecutivenetwork.com`;
const purposeLabels: Record<OutreachPurpose, string> = {
  invite_to_register: 'Invitation to register',
  introduce_mandate: 'Introduce a mandate',
  general: 'General message'
};

/** Templates are starting points — every one is editable before it is sent. */
function template(purpose: OutreachPurpose, name: string): {
  subject: string;
  body: string;
} {
  const first = name.trim().split(' ')[0] || 'there';
  switch (purpose) {
    case 'invite_to_register':
      return {
        subject: 'An invitation to join the Velnoris executive network',
        body: `Dear ${first},

I am writing from Velnoris Executive Network, a retained executive search firm. Your background stood out in our research, and I would like to invite you to register with us.

Registering places you in our research network so that we can share relevant confidential mandates with you directly. It takes a few minutes, and it is not an application — nothing about you is ever shared with an employer without your written authorization for that specific search.

You can register here: ${SITE}/register

If you would prefer a conversation first, simply reply to this message and I will make time.

With regards,

${SIGNATURE}`
      };
    case 'introduce_mandate':
      return {
        subject: 'A leadership appointment I thought you should see',
        body: `Dear ${first},

We are retained on a leadership appointment that I believe warrants your attention, and I wanted to bring it to you directly rather than let you find it in the market.

I can share the full brief once we have spoken. If it is not right for you, I would still value your view on who else it might suit.

Would a short call this week or next be possible?

With regards,

${SIGNATURE}`
      };
    default:
      return {
        subject: '',
        body: `Dear ${first},\n\n\nWith regards,\n\n${SIGNATURE}`
      };
  }
}
export function OutreachPanel() {
  const {
    threads,
    registrations,
    startOutreach,
    markDispatched
  } = useRegistry();
  const [purpose, setPurpose] = useState<OutreachPurpose>('invite_to_register');
  const [form, setForm] = useState({
    contactName: '',
    contactEmail: '',
    organization: '',
    subject: '',
    body: ''
  });
  const [touched, setTouched] = useState(false);
  const [copied, setCopied] = useState('');
  const [sending, setSending] = useState<{
    threadId?: string;
    to: string;
    subject: string;
    body: string;
  } | null>(null);
  const [status, setStatus] = useState<Note | null>(null);
  const [sender, setSender] = useState(getSenderEmail);
  const [client, setClient] = useState<MailClient>(getMailClient);
  const [editingSender, setEditingSender] = useState(false);
  const composed = () => ({
    contactName: form.contactName.trim(),
    contactEmail: form.contactEmail.trim(),
    organization: form.organization.trim(),
    subject: form.subject.trim() || purposeLabels[purpose],
    body: form.body.trim(),
    purpose
  });
  const clearForm = () => {
    setForm({
      contactName: '',
      contactEmail: '',
      organization: '',
      subject: '',
      body: ''
    });
    setTouched(false);
  };

  /** Keep it here to send later. */
  const saveOnly = () => {
    if (!ready) return;
    startOutreach(composed());
    clearForm();
    setStatus({
      tone: 'neutral',
      message: 'Saved as a draft. Send it from the history below whenever you are ready.'
    });
  };

  /** Record it and open it in the mailbox, ready to send. */
  const sendAndSave = () => {
    if (!ready) return;
    const draft = composed();
    const thread = startOutreach(draft);
    const opened = openCompose(draft.contactEmail, draft.subject, draft.body, client);
    markDispatched(thread.id);
    setStatus(opened ? {
      tone: 'positive',
      message: `Opened in ${mailClientLabels[client]}, addressed to ${draft.contactEmail}. Press send there and it arrives as an ordinary email from ${sender}.`
    } : {
      tone: 'neutral',
      message: 'Your browser blocked the new window. Allow pop-ups for this site, or use Other sending options.'
    });
    clearForm();
  };

  /** Re-open an existing record in the mailbox. */
  const sendExisting = (threadId: string, to: string, subject: string, body: string) => {
    const opened = openCompose(to, subject, body, client);
    markDispatched(threadId);
    setStatus(opened ? {
      tone: 'positive',
      message: `Opened in ${mailClientLabels[client]}, addressed to ${to}.`
    } : {
      tone: 'neutral',
      message: 'Your browser blocked the new window. Allow pop-ups for this site, or use the options button.'
    });
  };
  const outreach = useMemo(() => threads.filter(t => t.initiatedBy === 'owner'), [threads]);
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [key]: e.target.value
    }));
    if (key === 'subject' || key === 'body') setTouched(true);
  };

  /** Fill the draft from the template unless it has been hand-edited. */
  const applyTemplate = (next: OutreachPurpose, name = form.contactName) => {
    setPurpose(next);
    if (touched) return;
    const t = template(next, name);
    setForm(prev => ({
      ...prev,
      subject: t.subject,
      body: t.body
    }));
  };
  const ready = form.contactName.trim() && form.contactEmail.trim() && form.body.trim();
  const field = 'mt-2 w-full border border-navy-100 bg-white px-3.5 py-2.5 font-sans text-[0.875rem] text-ink placeholder:text-charcoal-200 transition-colors duration-120 hover:border-navy-300 focus:border-navy-500';
  const label = 'block font-sans text-[0.8125rem] font-medium text-ink';
  return <section className="mt-8" data-id="element-224" data-mp="29usocobj324l">
      <form className="border-l-2 border-ink bg-white p-6" onSubmit={e => {
      e.preventDefault();
      sendAndSave();
    }} data-id="element-225" data-mp="29vu9xmq6770k">
        <h2 className="font-serif text-[1.25rem] text-ink" data-id="element-226" data-mp="2ixbmww8a6sl1">Reach out to a candidate</h2>
        <p className="mt-1.5 max-w-2xl font-sans text-[0.8125rem] leading-relaxed text-charcoal-500" data-id="element-227" data-mp="3pcuhg57fy12p">
          Invite someone to register, introduce a mandate, or write to them directly.
        </p>

        {/* Sending identity — outreach need not come from the general mailbox. */}
        <div className="mt-5 border-l border-gold bg-parchment px-4 py-3" data-id="element-228" data-mp="3b58h7rfm7yc">
          {editingSender ? <div className="flex flex-wrap items-end gap-3" data-id="element-229" data-mp="3up15te45qa24">
              <div className="min-w-[16rem] flex-1" data-id="element-230" data-mp="3ungb9ohuer49">
                <label htmlFor="out-sender" className="block font-sans text-[0.75rem] font-medium text-ink" data-id="element-231" data-mp="3qmwg2qgtxxkr">
                  Send outreach from
                </label>
                <input id="out-sender" type="email" value={sender} onChange={e => setSender(e.target.value)} placeholder="you@velnorisexecutivenetwork.com" className="mt-1.5 w-full border border-navy-200 bg-white px-3 py-2 font-sans text-[0.875rem] text-ink" data-id="element-232" data-mp="3gq1j4c07g58l" />
              </div>
              <Button size="sm" type="button" onClick={() => {
            const next = sender.trim() || FIRM_EMAIL;
            setSender(next);
            setSenderEmail(next);
            setEditingSender(false);
            setStatus({
              tone: 'neutral',
              message: `Outreach will be composed from ${next}.`
            });
          }} data-id="element-233" data-mp="15y02x008ab3c">
                Use this address
              </Button>
              <button type="button" onClick={() => {
            setSender(getSenderEmail());
            setEditingSender(false);
          }} className="pb-2 font-sans text-[0.8125rem] text-charcoal-500 underline underline-offset-2 hover:text-ink" data-id="element-234" data-mp="15gxvew64yt1f">
                Cancel
              </button>
            </div> : <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-id="element-235" data-mp="2qwlqjjbwdgek">
              <p className="flex flex-wrap items-center gap-2 font-sans text-[0.8125rem] text-charcoal-700" data-id="element-236" data-mp="2vtvm7gpvqoau">
                Sending from <span className="text-ink" data-id="element-237" data-mp="35z3puyqpsyud">{sender}</span>
                <button type="button" onClick={() => setEditingSender(true)} className="text-charcoal-500 underline underline-offset-2 hover:text-ink" data-id="element-238" data-mp="310k1yssk9czd">
                  Change
                </button>
              </p>
              <span aria-hidden="true" className="text-charcoal-200" data-id="element-239" data-mp="3oott8a8jmsxw">
                ·
              </span>
              <label htmlFor="out-client" className="font-sans text-[0.8125rem] text-charcoal-700" data-id="element-240" data-mp="3jr1rkoi3jsgi">
                Compose in
              </label>
              <select id="out-client" value={client} onChange={e => {
            const next = e.target.value as MailClient;
            setClient(next);
            setMailClient(next);
          }} className="border border-navy-200 bg-white px-2.5 py-1.5 font-sans text-[0.8125rem] text-ink" data-id="element-241" data-mp="1ijc2e9eivl0">
                {(Object.keys(mailClientLabels) as MailClient[]).map(c => <option key={c} value={c} data-id="element-242" data-mp="3kzf77jdpsulb">
                    {mailClientLabels[c]}
                  </option>)}
              </select>
            </div>}
        </div>

        <div className="mt-6 flex flex-wrap gap-1.5" data-id="element-243" data-mp="3b4dfqgvyvgfk">
          {(Object.keys(purposeLabels) as OutreachPurpose[]).map(p => <button key={p} type="button" onClick={() => applyTemplate(p)} aria-pressed={purpose === p} className={`border px-3 py-1.5 font-sans text-[0.75rem] transition-colors duration-120 ${purpose === p ? 'border-ink bg-ink text-parchment' : 'border-navy-100 bg-white text-charcoal-500 hover:border-navy-400'}`} data-id="element-244" data-mp="cmzqc50nxe80">
              {purposeLabels[p]}
            </button>)}
        </div>

        {registrations.length ? <div className="mt-6" data-id="element-245" data-mp="xxxfcbimmjis">
            <label htmlFor="out-existing" className={label} data-id="element-246" data-mp="12uxhibccv24m">
              <span className="inline-flex items-center gap-1.5" data-id="element-247" data-mp="12vfnw0sqewq9">
                <UserSearchIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-248" data-mp="17t8rqn6kbend" />
                Prefill from someone already registered
              </span>
            </label>
            <select id="out-existing" defaultValue="" onChange={e => {
          const r = registrations.find(x => x.id === e.target.value);
          if (!r) return;
          setForm(prev => ({
            ...prev,
            contactName: r.fullName,
            contactEmail: r.email,
            organization: r.professionalTitle
          }));
          if (!touched) {
            const t = template(purpose, r.fullName);
            setForm(prev => ({
              ...prev,
              subject: t.subject,
              body: t.body
            }));
          }
        }} className={field} data-id="element-249" data-mp="1gv8e7v0cjaxp">
              <option value="" data-id="element-250" data-mp="h82mz5jsamp">Optional — or enter a new contact below</option>
              {registrations.map(r => <option key={r.id} value={r.id} data-id="element-251" data-mp="e94owmkjbqb6">
                  {r.fullName} — {r.email}
                </option>)}
            </select>
          </div> : null}

        <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-252" data-mp="o5hjdmnwm86z">
          <div data-id="element-253" data-mp="o83svrucuxru">
            <label htmlFor="out-name" className={label} data-id="element-254" data-mp="j7g1h527sex3">
              Name <span className="text-gold-700" data-id="element-255" data-mp="j6xzdrsvf6h9">*</span>
            </label>
            <input id="out-name" required value={form.contactName} onChange={e => {
            set('contactName')(e);
            if (!touched) {
              const t = template(purpose, e.target.value);
              setForm(prev => ({
                ...prev,
                contactName: e.target.value,
                subject: t.subject,
                body: t.body
              }));
            }
          }} className={field} data-id="element-256" data-mp="x5nu50s7wu2g" />
          </div>
          <div data-id="element-257" data-mp="2oun9lhlrtfzy">
            <label htmlFor="out-email" className={label} data-id="element-258" data-mp="2ot3rrmkguww2">
              Email <span className="text-gold-700" data-id="element-259" data-mp="2b3iduqlix7uw">*</span>
            </label>
            <input id="out-email" type="email" required value={form.contactEmail} onChange={set('contactEmail')} className={field} data-id="element-260" data-mp="2b15x2gri53jf" />
          </div>
          <div className="sm:col-span-2" data-id="element-261" data-mp="213rhskhbeksp">
            <label htmlFor="out-org" className={label} data-id="element-262" data-mp="216x63xi4dind">
              Current role or organization
            </label>
            <input id="out-org" value={form.organization} onChange={set('organization')} className={field} placeholder="Optional — for your own reference" data-id="element-263" data-mp="1s44ji7jvtv3v" />
          </div>
          <div className="sm:col-span-2" data-id="element-264" data-mp="1x5boxzyjyi7d">
            <label htmlFor="out-subject" className={label} data-id="element-265" data-mp="3mhm9e17rf422">
              Subject
            </label>
            <input id="out-subject" value={form.subject} onChange={set('subject')} className={field} data-id="element-266" data-mp="3rhrzve2amkik" />
          </div>
          <div className="sm:col-span-2" data-id="element-267" data-mp="1rdxujyl7yv0p">
            <label htmlFor="out-body" className={label} data-id="element-268" data-mp="1rcmv2ayt49fe">
              Message <span className="text-gold-700" data-id="element-269" data-mp="1mh7f9dh8o3gc">*</span>
            </label>
            <textarea id="out-body" required rows={12} value={form.body} onChange={set('body')} className={`${field} resize-y font-sans`} data-id="element-270" data-mp="1hg1pnoivkwb1" />
            <p className="mt-1.5 font-sans text-[0.75rem] text-charcoal-300" data-id="element-271" data-mp="1deyal48nj4av">
              Templates are a starting point. Edit freely — a message that reads as a template rarely earns a reply from
              a senior executive.
            </p>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-3 border-t border-navy-100 pt-5" data-id="element-272" data-mp="yr3f5g39elu3">
          <Button type="submit" disabled={!ready} data-id="element-273" data-mp="tsjs1ccxj405">
            <SendIcon aria-hidden="true" className="h-4 w-4" data-id="element-274" data-mp="ppnfa2a2m244" />
            Send message
          </Button>
          <Button type="button" variant="secondary" disabled={!ready} onClick={() => saveOnly()} data-id="element-275" data-mp="kqsniau4l37v">
            Save as draft
          </Button>
          {ready ? <button type="button" onClick={() => setSending({
          to: form.contactEmail.trim(),
          subject: form.subject.trim() || purposeLabels[purpose],
          body: form.body
        })} className="inline-flex items-center gap-1.5 font-sans text-[0.8125rem] text-charcoal-500 underline underline-offset-2 hover:text-ink" data-id="element-276" data-mp="2wdmgk05gfsui">
              <MailIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-277" data-mp="2ho81cbf76h77" />
              Other sending options
            </button> : null}
        </div>

        {status ? <p role="status" className={`mt-4 flex items-start gap-2 font-sans text-[0.8125rem] leading-relaxed ${status.tone === 'positive' ? 'text-state-positive' : 'text-charcoal-700'}`} data-id="element-278" data-mp="2rejbifph5mmj">
            {status.tone === 'positive' ? <CheckIcon aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-279" data-mp="3295t037xvw6x" /> : <AlertCircleIcon aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-280" data-mp="2xaung4oog7ui" />}
            {status.message}
          </p> : <p className="mt-3 max-w-2xl font-sans text-[0.75rem] leading-relaxed text-charcoal-500" data-id="element-281" data-mp="3b3txvs9lrbv7">
            Send message opens it in your mailbox, addressed and written — press send there and the candidate receives
            an ordinary email from {sender}. Save as draft keeps it here for later.
          </p>}
      </form>

      <h3 className="mt-10 font-serif text-[1.125rem] text-ink" data-id="element-282" data-mp="2sbrhmi0vhxlh">
        Outreach history{' '}
        <span className="font-sans text-[0.75rem] text-charcoal-300" data-id="element-283" data-mp="2nbnahiluuf7x">{outreach.length} contacted</span>
      </h3>

      {!outreach.length ? <p className="mt-4 border border-dashed border-navy-200 bg-white p-10 text-center font-sans text-[0.875rem] text-charcoal-500" data-id="element-284" data-mp="7jfxyd4nyae9">
          No outreach yet. Anyone you write to appears here, with whether the message has been sent and whether they
          have replied.
        </p> : <ul className="mt-4 space-y-4" data-id="element-285" data-mp="f5uuhg28n0we">
          {outreach.map(t => {
        const replied = t.messages.some(m => m.from === 'client');
        const last = t.messages[t.messages.length - 1];
        return <li key={t.id} className="border-l-2 border-ink bg-white p-5" data-id="element-286" data-mp="ttfw15jgss27">
                <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-287" data-mp="tvivshaf5zk4">
                  <div className="min-w-0" data-id="element-288" data-mp="yrzlvh16inzu">
                    <div className="flex flex-wrap items-center gap-2.5" data-id="element-289" data-mp="k3wblvg3sq58">
                      <h4 className="font-serif text-[1.0625rem] text-ink" data-id="element-290" data-mp="k1igxnpc5fy7">{t.contactName}</h4>
                      <span className="font-sans text-[0.75rem] text-charcoal-300" data-id="element-291" data-mp="p0mpqd8clbo0">{t.reference}</span>
                      {replied ? <span className="border border-state-positive/40 px-2 py-0.5 font-sans text-micro uppercase tracking-[0.12em] text-state-positive" data-id="element-292" data-mp="1hjio3lndpy0t">
                          Replied
                        </span> : t.dispatched ? <span className="border border-navy-200 px-2 py-0.5 font-sans text-micro uppercase tracking-[0.12em] text-charcoal-500" data-id="element-293" data-mp="38tlt887ydch8">
                          Sent · awaiting reply
                        </span> : <span className="border border-gold bg-gold-100 px-2 py-0.5 font-sans text-micro uppercase tracking-[0.12em] text-gold-700" data-id="element-294" data-mp="ekb1bylscu5">
                          Draft · not sent
                        </span>}
                    </div>
                    <p className="mt-1.5 font-sans text-[0.875rem] text-charcoal-700" data-id="element-295" data-mp="3ili2mffhop0r">
                      <a href={`mailto:${t.contactEmail}`} className="underline underline-offset-2" data-id="element-296" data-mp="2c564geco3756">
                        {t.contactEmail}
                      </a>
                      {t.organization ? ` · ${t.organization}` : ''}
                      {t.purpose ? ` · ${purposeLabels[t.purpose]}` : ''}
                    </p>
                    <p className="mt-2 font-sans text-[0.875rem] text-ink" data-id="element-297" data-mp="ro6oajcsmktj">{t.subject}</p>
                    <p className="mt-1 max-w-2xl truncate font-sans text-[0.8125rem] text-charcoal-500" data-id="element-298" data-mp="vogsprkxt36a">{last.body}</p>
                    <p className="mt-2 font-sans text-[0.75rem] text-charcoal-300" data-id="element-299" data-mp="10g8meaze3p0n">{formatWhen(t.updatedAt)}</p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2" data-id="element-300" data-mp="15gmj4wvjjcjd">
                    <Button size="sm" onClick={() => sendExisting(t.id, t.contactEmail, t.subject, t.messages[0].body)} data-id="element-301" data-mp="1acblf7yy37jd">
                      <SendIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-302" data-mp="1fdhjzmfadp0t" />
                      {t.dispatched ? 'Send again' : 'Send now'}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setSending({
                threadId: t.id,
                to: t.contactEmail,
                subject: t.subject,
                body: t.messages[0].body
              })} data-id="element-303" data-mp="3h5491ifmb9un">
                      <MailIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-304" data-mp="3ev0xvk3wnpws" />
                      <span className="sr-only" data-id="element-305" data-mp="3jpev7s24o743">Other sending options for {t.contactName}</span>
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => {
                navigator.clipboard?.writeText(`${t.subject}\n\n${t.messages[0].body}`);
                setCopied(t.id);
                window.setTimeout(() => setCopied(''), 1600);
              }} data-id="element-306" data-mp="3oozzmi02h7o7">
                      {copied === t.id ? <CheckIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-307" data-mp="287vnpnhilhzq" /> : <CopyIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-308" data-mp="20q7k6g0rwonu" />}
                      {copied === t.id ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
              </li>;
      })}
        </ul>}

      <p className="mt-8 max-w-3xl border-t border-navy-100 pt-5 font-sans text-[0.75rem] leading-relaxed text-charcoal-500" data-id="element-309" data-mp="1m11dy83ipy6g">
        Outreach leaves from {sender} as ordinary email — no forwarding service is involved, so what the candidate
        receives is a normal message from a person, and replies thread back to you as usual. Approaching someone who
        has not registered is
        legitimate research contact — keep it proportionate, identify the firm, and honour any request not to be
        contacted again.
      </p>

      {sending ? <SendDialog to={sending.to} subject={sending.subject} body={sending.body} onClose={() => setSending(null)} onSent={() => {
      if (sending.threadId) markDispatched(sending.threadId);
      setSending(null);
    }} data-id="element-310" data-mp="y8b4c5j37qzc" /> : null}
    </section>;
}